import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PageProps } from '@/lib/market/builders/shared'
import type { IndicesData, SectorSeries } from '@/lib/market/builders/indices'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Panel, PanelBody, PanelHeader } from '@/components/generals/panel'
import { buildIndices } from '@/lib/market/builders/indices'
import { withMarket } from '@/lib/market/builders/shared'
import { ChangeText } from '@/components/market/change'
import { formatLevel, formatNumber, formatPercent } from '@/lib/market/format'
import IndexCards from '@/components/market/index-cards'
import IndexChartCard from '@/components/market/index-chart-card'
import IndexComparison from '@/components/indices/index-comparison'
import IndexConstituents from '@/components/indices/index-constituents'
import PageHeader from '@/components/generals/page-header'
import Sparkline from '@/components/charts/sparkline'
import Unavailable from '@/components/market/unavailable'

type IndicesProps = PageProps<IndicesData>

export const getServerSideProps: GetServerSideProps<IndicesProps> = async context => withMarket(snapshot => buildIndices(snapshot), { req: context.req })

const METHODOLOGY = [
    {
        code: 'KASI',
        formula: 'Level = Base x Sum(price x shares in issue) / Sum(reference price x shares in issue)',
        notes: 'Every ordinary share is included at its full market value, so the largest companies carry the most weight. Real estate investment trusts are excluded.'
    },
    {
        code: 'K20',
        formula: 'Level = Base x (Product of price / reference price) ^ (1 / 20)',
        notes: 'A geometric mean gives each of the twenty constituents the same influence. A 10 percent move in any constituent moves the index by roughly 0.5 percent.'
    },
    {
        code: 'K25',
        formula: 'Level = Base x Sum(weight x price / reference price), with each weight capped at 10%',
        notes: 'Weights start from market value. Any constituent above 10 percent is capped and the excess is shared out across the rest in proportion to their size.'
    }
]

const SectorTable = ({ sectors }: { sectors: SectorSeries[] }): ReactNode => {
    const ordered = [...sectors].sort((a, b) => (b.perf.ytd ?? 0) - (a.perf.ytd ?? 0))

    return (
        <div className='-mx-4 overflow-x-auto px-4 scroll-thin sm:mx-0 sm:px-0'>
            <table className='w-full min-w-[720px] text-left text-[13px]'>
                <caption className='sr-only'>Sector indices, base 1,000 at the reference date</caption>
                <thead>
                    <tr className='border-b border-border text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                        <th scope='col' className='py-2 pr-3 font-medium'>Sector</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>Level</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>Today</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>1M</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>3M</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>YTD</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>1Y</th>
                        <th scope='col' className='py-2 font-medium'>3 months</th>
                    </tr>
                </thead>
                <tbody>
                    {ordered.map(sector => (
                        <tr key={sector.slug} className='border-b border-border/60 last:border-0'>
                            <th scope='row' className='py-2 pr-3 font-normal'>
                                <Link href={`/equities?sector=${sector.slug}`} className='group block'>
                                    <span className='text-foreground group-hover:text-primary'>
                                        {sector.id ? <span data-mdlfy-cms={`sectors:${sector.id}:name`}>{sector.name}</span> : sector.name}
                                    </span>
                                    <span className='block text-xs text-muted-foreground'>{sector.members} {sector.members === 1 ? 'company' : 'companies'}</span>
                                </Link>
                            </th>
                            <td className='py-2 pr-3 text-right tabular-nums text-foreground'>{formatLevel(sector.levels[sector.levels.length - 1])}</td>
                            <td className='py-2 pr-3 text-right'><ChangeText value={sector.changePct} /></td>
                            <td className='py-2 pr-3 text-right'><ChangeText value={sector.perf.m1} decimals={1} /></td>
                            <td className='py-2 pr-3 text-right'><ChangeText value={sector.perf.m3} decimals={1} /></td>
                            <td className='py-2 pr-3 text-right'><ChangeText value={sector.perf.ytd} decimals={1} /></td>
                            <td className='py-2 pr-3 text-right'><ChangeText value={sector.perf.y1} decimals={1} /></td>
                            <td className='py-2'><Sparkline values={sector.levels.slice(-63)} width={96} height={26} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

const Indices: FC<IndicesProps> = ({ data, error }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Indices'
                description='The Kilele All Share Index, Kilele 20 and Kilele 25 with live levels, constituents, weights, contributions and sector indices.'
            />

            {!data ? <Unavailable message={error} /> : (
                <>
                    <PageHeader
                        eyebrow='Benchmarks'
                        title='Indices'
                        description='Three benchmarks measure the market from different angles: by total value, by the typical blue chip, and by the largest companies with a cap on concentration.'
                    />

                    <IndexCards indices={data.indices} className='mb-4' />

                    <Panel id='comparison' labelledBy='comparison-title' className='mb-4'>
                        <PanelHeader id='comparison-title' eyebrow='Comparison' title='All three indices rebased to 100' description='Rebasing puts every index on one scale so the lines compare percentage performance directly.' />
                        <PanelBody>
                            <IndexComparison indices={data.indices} />
                        </PanelBody>
                    </Panel>

                    {data.indices.map(index => (
                        <section key={index.code} id={index.code.toLowerCase()} aria-labelledby={`${index.code}-title`} className='mb-4 scroll-mt-24'>
                            <div className='grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]'>
                                <Panel as='div'>
                                    <PanelHeader
                                        id={`${index.code}-title`}
                                        eyebrow={index.method}
                                        title={index.name}
                                        description={index.description}
                                    />
                                    <PanelBody>
                                        <IndexChartCard indices={[index]} defaultCode={index.code} showSwitcher={false} height={240} />

                                        <dl className='mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4'>
                                            {[
                                                { label: '52-week high', value: formatLevel(index.high52) },
                                                { label: '52-week low', value: formatLevel(index.low52) },
                                                { label: 'Volatility, 1Y', value: formatPercent(index.stats.volatility, 1, false) },
                                                { label: 'Max drawdown, 1Y', value: formatPercent(index.stats.maxDrawdown, 1) },
                                                { label: 'Sharpe ratio, 1Y', value: formatNumber(index.stats.sharpe, 2) },
                                                { label: 'Best session', value: formatPercent(index.stats.bestDay, 2) },
                                                { label: 'Worst session', value: formatPercent(index.stats.worstDay, 2) },
                                                { label: index.code === 'KASI' ? 'Positive sessions' : 'Beta to KASI', value: index.code === 'KASI' ? formatPercent(index.stats.upDays, 1, false) : formatNumber(index.stats.beta ?? 0, 2) }
                                            ].map(item => (
                                                <div key={item.label} className='bg-card px-3.5 py-2.5'>
                                                    <dt className='text-[11px] text-muted-foreground'>{item.label}</dt>
                                                    <dd className='mt-0.5 text-sm font-semibold tabular-nums text-foreground'>{item.value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </PanelBody>
                                </Panel>

                                <Panel as='div'>
                                    <PanelHeader eyebrow='Attribution' title='Constituents and contribution' description='Live weights and the index points each share has added or removed this session.' />
                                    <PanelBody>
                                        <IndexConstituents index={index} />
                                    </PanelBody>
                                </Panel>
                            </div>
                        </section>
                    ))}

                    <Panel id='sector-indices' labelledBy='sector-indices-title' className='mb-4'>
                        <PanelHeader id='sector-indices-title' eyebrow='Sector indices' title='Performance by sector' description='Each sector index is weighted by market value and set to 1,000 at the reference date.' />
                        <PanelBody>
                            <SectorTable sectors={data.sectors} />
                        </PanelBody>
                    </Panel>

                    <Panel id='methodology' labelledBy='methodology-title'>
                        <PanelHeader id='methodology-title' eyebrow='Methodology' title='How each index is calculated' />
                        <PanelBody className='grid gap-4 lg:grid-cols-3'>
                            {METHODOLOGY.map(item => {
                                const index = data.indices.find(entry => entry.code === item.code)

                                return (
                                    <article key={item.code} className='rounded-lg border border-border bg-background/50 p-4'>
                                        <p className='font-mono text-xs font-semibold text-primary'>{item.code}</p>
                                        <h3 className='mt-1 text-sm font-semibold text-foreground'>{index?.name}</h3>
                                        <p className='mt-3 rounded-md bg-secondary px-3 py-2 font-mono text-[11px] leading-relaxed text-foreground/90'>{item.formula}</p>
                                        <p className='mt-3 text-[13px] leading-relaxed text-muted-foreground'>{item.notes}</p>
                                        {index && <p className='mt-3 text-xs text-muted-foreground'>{index.members} constituents. Base value {formatNumber(index.target, index.target < 1000 ? 1 : 0)} at the reference date.</p>}
                                    </article>
                                )
                            })}
                        </PanelBody>
                    </Panel>
                </>
            )}
        </Layout>
    )
}

export default Indices
